import { OrganizationType } from '../types/organization.type';
import type { KnowledgeArticle } from '../types/knowledge-base.type';

const ALL_ORGANIZATION_TYPES = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.TRUCK,
  OrganizationType.RECYCLER,
] as const;

export const KNOWLEDGE_BASE_CATEGORY_LABELS: Record<KnowledgeArticle['category'], string> = {
  technical_support: 'Technical Support',
  data_esg_question: 'Data & ESG',
  training_hse: 'Training & HSE',
  complaints: 'Complaints',
  suggestions: 'Suggestions',
  general_inquiry: 'General',
};

export const KNOWLEDGE_BASE_ARTICLES: KnowledgeArticle[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    summary: 'Overview of ESG KPIs, charts, and dashboard layout.',
    content:
      'The Dashboard gives you a snapshot of your organization\'s environmental performance. KPI cards and charts reflect data saved in Data Source and other ESG modules.\n\nSelect a guide below for step-by-step instructions.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'kpi', 'overview'],
    children: [
      'dashboard-overview',
      'dashboard-widget-customization',
      'dashboard-kpi-limits',
      'dashboard-branches-compare',
      'dashboard-carbon-avoidance',
    ],
  },
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    summary: 'Full guided walkthrough of the dashboard (same as the welcome tour).',
    content:
      'Use Go to Dashboard for a guided walkthrough of the same tour shown in the welcome window:\n\n1. Monthly tab — pick a month, open Customize Dashboard, set KPI Limits, then review ESG cards and charts.\n\n2. Branches tab — review branch metrics and open Compare Branches.\n\n3. Carbon Avoidance tab — set the date range and stream, then review the results.\n\nFor deeper focus on one area, open KPI Limits, Branches / Compare, or Carbon Avoidance below.\n\nTip: Dashboard figures update when you save new utility or operational data in Data Source.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'overview', 'tabs'],
  },
  {
    id: 'dashboard-widget-customization',
    title: 'Customize Dashboard',
    summary: 'Choose which widgets and charts appear on your dashboard.',
    content:
      '1. On the Dashboard, click Customize Dashboard (top right of the page).\n\n2. The dialog customizes the tab you are on — Monthly, Overview, Scopes, Branches, or Carbon Avoidance.\n\n3. Check or uncheck the widgets that appear on that tab.\n\n4. Click Save Changes. Your selection is saved for the current browser session.\n\nTip: If a section shows “No widgets selected”, open Customize Dashboard and add the metrics you need.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'widgets', 'customize'],
  },
  {
    id: 'dashboard-kpi-limits',
    title: 'KPI Limits',
    summary: 'Set targets and review enabled KPI limits on the Monthly dashboard.',
    content:
      'Use Go to KPI Limits for a short guided walkthrough:\n\n1. On the Monthly tab, open KPI Limits.\n\n2. Add or edit a limit — choose the metric, target value, and whether it is enabled.\n\n3. Review the list of limits; enabled KPIs appear on the dashboard cards.\n\nTip: KPI Limits help you track progress against monthly targets for the selected organization.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'kpi', 'limits', 'targets'],
  },
  {
    id: 'dashboard-branches-compare',
    title: 'Branches & Compare',
    summary: 'Review per-branch metrics and compare branches side by side.',
    content:
      'Use Go to Branches for a short guided walkthrough:\n\n1. Open the Branches tab on the Dashboard.\n\n2. Review the branch table and KPI cards for each site.\n\n3. Click Compare Branches to open the comparison dialog and pick branches to compare.\n\nTip: Compare works best when at least two branches have data for the selected month.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'branches', 'compare'],
  },
  {
    id: 'dashboard-carbon-avoidance',
    title: 'Carbon Avoidance Tab',
    summary: 'Set the carbon period and stream, then review avoidance results.',
    content:
      'Use Go to Carbon Avoidance for a short guided walkthrough:\n\n1. Open the Carbon Avoidance tab on the Dashboard.\n\n2. Set the start and end dates for the reporting window.\n\n3. Choose a waste stream filter if you want a focused view.\n\n4. Review the carbon avoidance totals and charts.\n\nNote: The Carbon Avoidance Report PDF uses this same period (not the Reports month picker).',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['dashboard', 'carbon', 'avoidance'],
  },
  {
    id: 'esg-reports',
    title: 'Extracting Reports',
    summary: 'Generate and download ESG PDF reports.',
    content:
      'ESG Reports generates PDF reports for the organization selected in the header.\n\nComplete Data Source entries for the month before you generate a report.\n\nUse Customize Report to choose which sections appear in each PDF, then select a report below to download, or open Metric Definitions to learn what each metric means and how to search the catalog.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['reports', 'pdf', 'esg'],
    children: [
      'report-general-esg',
      'report-waste-circularity',
      'report-carbon-avoidance',
      'report-metric-definitions',
    ],
  },
  {
    id: 'report-general-esg',
    title: 'General ESG Performance Report',
    summary: 'Monthly PDF with waste, utilities, monitoring, and training.',
    content:
      'Definition — Monthly PDF with a broad view of your organization\'s environmental performance.\nContent — Waste handled, utility consumption and costs, monitoring quality, and training hours in one file.\nPeriod — Reports page month picker.\n\nWhat\'s inside:\n• Executive summary — headline waste, utility, and training totals\n• Waste — diversion, recycling, composition, and fill-level counts\n• Utilities — water, electricity, oil, and gas with SAR costs\n• Monitoring & training — quality KPIs and recorded training hours\n\nTip: Missing or zero metrics usually mean Data Source fields were not saved for that month.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['reports', 'pdf', 'general', 'esg'],
  },
  {
    id: 'report-waste-circularity',
    title: 'Waste & Circularity Report',
    summary: 'Waste-focused PDF aligned with GRI 306 themes.',
    content:
      'Definition — Waste-focused PDF mapped to GRI 306 disclosure themes.\nContent — How much waste was handled, diverted, and recycled, including branch detail and GRI coverage notes. Not a formal GRI assurance statement.\nPeriod — Reports page month picker.\n\nWhat\'s inside:\n• Executive summary — waste handled, diverted, recycled, and diversion rate\n• Waste flows — incoming totals, composition, and diversion/recycling figures\n• Branches — per-branch performance when data exists\n• GRI 306 mapping — how platform metrics relate to disclosure themes\n\nTip: Missing or zero metrics usually mean Data Source fields were not saved for that month.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['reports', 'pdf', 'waste', 'gri306'],
  },
  {
    id: 'report-carbon-avoidance',
    title: 'Carbon Avoidance Report',
    summary: 'PDF estimating carbon avoided (tCO₂e) from diversion runs.',
    content:
      'Definition — PDF estimating carbon avoided (tCO₂e) from completed waste diversion runs.\nContent — Total avoidance, breakdown by waste stream, and calculation details for audit review.\nPeriod — Dashboard → Carbon Avoidance (not the Reports month picker).\n\nWhat\'s inside:\n• Executive summary — total avoided, weight diverted, and run counts\n• Streams — share of avoidance by waste type\n• Audit appendix — run status and emission factors used\n\nNote: Carbon Avoidance uses the Dashboard period, not the Reports month picker.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['reports', 'pdf', 'carbon'],
  },
  {
    id: 'report-metric-definitions',
    title: 'Metric Definitions',
    summary: 'Browse and search metric definitions used in ESG reports.',
    content:
      'Use Go to Metric Definitions for a short guided walkthrough:\n\n1. Open the Metric Definitions tab on ESG Reports.\n\n2. Use Search to filter metrics by name or keyword.\n\n3. Open a metric card to read its definition, unit, and where it appears in reports.\n\nTip: Metric Definitions explain the numbers behind PDF downloads — use them when a report value looks unfamiliar.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['reports', 'metrics', 'definitions', 'search'],
  },
  {
    id: 'data-source',
    title: 'Data Source (Utilities)',
    summary: 'Enter utility data, upload proofs, and manage branch settings.',
    content:
      'Data Source is where you record monthly utility consumption (water, electricity, oil, gas) per branch. Saved data feeds the Dashboard and ESG Reports.\n\nSelect a guide below for step-by-step instructions.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['data', 'utilities', 'entry'],
    children: [
      'data-source-intro',
      'utility-data-entry',
      'utility-proofs',
      'data-source-settings',
    ],
  },
  {
    id: 'data-source-intro',
    title: 'Data Source Overview',
    summary: 'Navigate branches, months, and utility tabs.',
    content:
      '1. Open Data Source in the sidebar.\n\n2. Select a branch (collection site) from the branch selector.\n\n3. Choose the reporting month with the calendar picker.\n\n4. Use the utility tabs — Water, Electricity, Oil, Gas — to enter consumption and cost for that branch and month.\n\n5. Switch to Utility Proofs to attach supporting documents, or Settings to configure branch-level options.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['data', 'branch', 'month'],
  },
  {
    id: 'utility-data-entry',
    title: 'Enter & save utility data',
    summary: 'Record consumption for a branch and month, then save.',
    content:
      '1. Go to Data Source and select a branch and reporting month.\n\n2. Open the utility tab you need (Water, Electricity, Oil, or Gas).\n\n3. Enter consumption values and amounts in SAR where applicable.\n\n4. Repeat for each utility type required for that branch.\n\n5. Click Save and wait for the success confirmation before switching branch or month.\n\nImportant: Unsaved changes are lost if you change branch, month, or leave the page.\n\nTip: Enter data consistently each month so dashboard trends and PDF reports remain comparable.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['data', 'water', 'electricity', 'gas', 'oil', 'save'],
  },
  {
    id: 'utility-proofs',
    title: 'Utility Proofs',
    summary: 'Upload supporting documents for utility entries.',
    content:
      '1. In Data Source, select branch and reporting month.\n\n2. Open the Utility Proofs tab.\n\n3. Upload bills, invoices, or meter readings that support your utility figures.\n\n4. Review uploaded files in the list and remove outdated proofs if needed.\n\nBest practice: Keep proofs aligned with the same month as your consumption entries for audit readiness.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['data', 'proofs', 'upload'],
  },
  {
    id: 'data-source-settings',
    title: 'Data Source Settings',
    summary: 'Configure branch-level utility settings.',
    content:
      '1. Go to Data Source and select a branch.\n\n2. Open the Settings tab.\n\n3. Adjust branch-specific options such as measurement preferences or defaults shown on utility forms.\n\n4. Save any changes before leaving the tab.\n\nTip: Configure settings once per new branch, then focus on monthly data entry.',
    category: 'data_esg_question',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['data', 'settings', 'branch'],
  },
  {
    id: 'employee-training',
    title: 'Employee Training',
    summary: 'Track training hours and sessions across your organization.',
    content:
      'Employee Training shows a summary of staff training activity. Record sessions, open employee detail pages, and organize training by category.\n\nSelect a guide below for step-by-step instructions.',
    category: 'training_hse',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['training', 'employees'],
    children: [
      'employee-training-overview',
      'record-training-session',
      'employee-training-detail',
      'training-categories',
    ],
  },
  {
    id: 'employee-training-overview',
    title: 'Training Overview',
    summary: 'Browse the organization training summary table.',
    content:
      '1. Open Employee training in the sidebar.\n\n2. Review the summary table listing employees, total training hours, and last session date.\n\n3. Use the search box to find a specific employee.\n\n4. Click column headers to sort by name, hours, or date.\n\n5. Click a row or the view action to open an employee\'s training detail page.',
    category: 'training_hse',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['training', 'overview', 'table'],
  },
  {
    id: 'record-training-session',
    title: 'Record Training Session',
    summary: 'Log a new training session for an employee.',
    content:
      '1. On the Employee training page, click Record Training (top right).\n\n2. Select the employee, training category, and session date.\n\n3. Enter training hours and any required notes.\n\n4. Submit the form. The summary table updates with the new totals.\n\nTip: Record sessions promptly after completion to keep compliance reports accurate.',
    category: 'training_hse',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['training', 'record', 'session'],
  },
  {
    id: 'employee-training-detail',
    title: 'Employee Training Detail',
    summary: 'View and manage an individual employee\'s training history.',
    content:
      '1. From the training overview table, open an employee\'s detail page.\n\n2. Review their session history, categories, and cumulative hours.\n\n3. Record additional sessions from this page where available.\n\n4. Use the back navigation to return to the organization summary.\n\nThe detail page is the single place to audit one employee\'s full training record.',
    category: 'training_hse',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['training', 'employee', 'detail'],
  },
  {
    id: 'training-categories',
    title: 'Training Categories',
    summary: 'Understand how training types are grouped.',
    content:
      'Training categories classify sessions (for example safety, environmental awareness, or operational skills).\n\n• Choose the correct category when recording a session — it affects reporting and filters.\n• Categories appear in session forms and on employee detail views.\n• Consistent categorization helps leadership review compliance by topic.\n\nIf you are unsure which category to use, pick the closest match and add details in the session notes.',
    category: 'training_hse',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['training', 'categories'],
  },
  {
    id: 'health-safety',
    title: 'Health & Safety',
    summary: 'Complete weekly HSE reports per branch.',
    content:
      'Health & Safety captures weekly operational safety data for each branch. Fill the report table, save entries, and refer to standard HSE terms.\n\nSelect a guide below for step-by-step instructions.',
    category: 'training_hse',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['hse', 'safety', 'weekly'],
    children: ['hse-weekly-report', 'hse-terms'],
  },
  {
    id: 'hse-weekly-report',
    title: 'Complete weekly HSE report',
    summary: 'Select branch and week, fill the table, then save.',
    content:
      '1. Open Health & Safety in the sidebar.\n\n2. Select a branch from the branch selector.\n\n3. Choose the reporting week with the week picker.\n\n4. Fill each table row — enter the weekly count and optional notes. Use HSE Terms for definitions.\n\n5. Click Save to store the report.\n\nA success message confirms when the weekly report is saved.\n\nTip: Complete reports at the end of each week while details are still fresh.',
    category: 'training_hse',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['hse', 'weekly', 'report', 'table'],
  },
  {
    id: 'hse-terms',
    title: 'HSE Terms',
    summary: 'Key definitions used in weekly safety reporting.',
    content:
      'Reference — common HSE report terms:\n\n• Incident — an unplanned event resulting in or with potential for injury or damage\n• Near miss — an event that could have caused harm but did not\n• Observation — a documented safety walk or checklist finding\n• Corrective action — steps taken to address an identified risk\n• Lost-time injury — injury causing missed work time\n\nUse consistent definitions across branches so consolidated HSE trends remain reliable.',
    category: 'training_hse',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['hse', 'terms', 'definitions'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    summary: 'View alerts and manage read status.',
    content:
      'Notifications keep you informed about platform activity. Open the bell icon in the header to review unread items.\n\nSelect a guide below for step-by-step instructions.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['notifications', 'alerts'],
    children: ['notification-bell', 'mark-notifications-read'],
  },
  {
    id: 'notification-bell',
    title: 'Notification Bell',
    summary: 'Open and browse notifications from the header.',
    content:
      '1. Click the bell icon in the top header bar.\n\n2. The badge shows how many unread notifications you have.\n\n3. Scroll the list to read recent alerts for your organization.\n\n4. Click outside the menu or press Escape to close it.\n\nNotifications are scoped to the organization currently selected in the header.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['notifications', 'bell'],
  },
  {
    id: 'mark-notifications-read',
    title: 'Mark Notifications as Read',
    summary: 'Clear unread items individually or in bulk.',
    content:
      '1. Open the notification bell menu.\n\n2. Click a single notification to mark it as read — it disappears from the unread list.\n\n3. Use Mark all as read to clear every unread notification at once.\n\n4. The badge count updates immediately after items are marked read.\n\nTip: Marking notifications as read does not delete them from the system history on the backend.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['notifications', 'read'],
  },
  {
    id: 'account-settings',
    title: 'Account & Preferences',
    summary: 'Change the interface language, switch theme, and manage your account.',
    content:
      'Open the account menu from your profile at the bottom of the sidebar to change the interface language, switch between light and dark mode, and sign out. Your profile shows your username and active organization.\n\nSelect a guide below for step-by-step instructions.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['account', 'language', 'theme', 'appearance', 'preferences', 'settings'],
    children: ['change-language', 'appearance-theme'],
  },
  {
    id: 'change-language',
    title: 'Change Language',
    summary: 'Switch the platform language across the supported languages.',
    content:
      '1. Click your profile at the bottom of the sidebar to open the account menu.\n\n2. Click Languages.\n\n3. Choose your language from the list: English, Arabic, Urdu, Hindi, Bengali, Sinhala, or Nepali.\n\n4. The interface updates instantly, and your choice is remembered the next time you sign in.\n\nNote: Arabic and Urdu automatically switch the layout to right-to-left (RTL) for comfortable reading. The interface and Help content are available in all supported languages.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['language', 'arabic', 'urdu', 'rtl', 'localization', 'translation'],
  },
  {
    id: 'appearance-theme',
    title: 'Light & Dark Mode',
    summary: 'Switch between light and dark appearance.',
    content:
      '1. Click your profile at the bottom of the sidebar to open the account menu.\n\n2. Use the Dark mode switch to toggle between the light and dark themes.\n\n3. Your preference is saved automatically and applied on every visit.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['theme', 'dark mode', 'light mode', 'appearance'],
  },
];

const CHILD_ARTICLE_IDS = new Set(
  KNOWLEDGE_BASE_ARTICLES.flatMap((article) => article.children ?? [])
);

export const getKnowledgeArticlesForOrganizationType = (
  organizationType: OrganizationType
): KnowledgeArticle[] =>
  KNOWLEDGE_BASE_ARTICLES.filter((article) => article.organizationTypes.includes(organizationType));

export const getTopLevelKnowledgeArticlesForOrganizationType = (
  organizationType: OrganizationType
): KnowledgeArticle[] =>
  getKnowledgeArticlesForOrganizationType(organizationType).filter(
    (article) => !CHILD_ARTICLE_IDS.has(article.id)
  );

export const getKnowledgeArticleById = (id: string): KnowledgeArticle | undefined =>
  KNOWLEDGE_BASE_ARTICLES.find((article) => article.id === id);

export const getKnowledgeArticleChildren = (
  article: KnowledgeArticle,
  organizationType: OrganizationType
): KnowledgeArticle[] => {
  if (!article.children?.length) return [];
  const allowed = new Set(
    getKnowledgeArticlesForOrganizationType(organizationType).map((item) => item.id)
  );
  return article.children
    .map((childId) => getKnowledgeArticleById(childId))
    .filter((child): child is KnowledgeArticle => Boolean(child && allowed.has(child.id)));
};

export const getKnowledgeArticleParentId = (articleId: string): string | undefined => {
  const parent = KNOWLEDGE_BASE_ARTICLES.find((article) => article.children?.includes(articleId));
  return parent?.id;
};
